import React, { useCallback, useEffect, useRef, useState } from 'react';

import ErrorState from '../../../commodity/components/ui/ErrorState';
import LoadingState from '../../../commodity/components/ui/LoadingState';
import ReviewCard from '../../../commodity/components/ui/ReviewCard';
import StarRating from '../../../commodity/components/ui/StarRating';
import { PublicReview, RatingAggregate, ReviewSubject, SubjectReviews } from '../api/reviews';

interface Props {
  subject: ReviewSubject;
  subjectId: string;
  /**
   * Injected so the same section serves the teacher page and the course page.
   * Called with a cursor for every page after the first.
   */
  load: (id: string, cursor?: string) => Promise<SubjectReviews>;
  title: string;
}

/**
 * Reviews for one subject (ADR-007 Epic 12).
 *
 * Renders three genuinely different situations rather than collapsing them:
 * still loading, nobody has reviewed yet, and the request failed. A course
 * with no reviews and a course whose reviews would not load look identical if
 * an empty list is used for both, and the second one is a lie about a teacher's
 * reputation.
 */
const ReviewsSection: React.FC<Props> = ({ subject, subjectId, load, title }) => {
  const [reviews, setReviews] = useState<PublicReview[]>([]);
  const [aggregate, setAggregate] = useState<RatingAggregate | undefined>();
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  // The subject a response belongs to. A "Ver más" still in flight when the
  // page moves to another teacher must not append that teacher's reviews here.
  const current = useRef(subjectId);
  current.current = subjectId;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    setMoreError(null);
    try {
      const page = await load(subjectId);
      if (current.current !== subjectId) return;
      setReviews(page.reviews ?? []);
      setAggregate(page.aggregate);
      setNextCursor(page.nextCursor);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [load, subjectId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * The next page of the same subject — PRT-06-FR-05, AC-11.
   *
   * The server returns reviews a page at a time (PRT-BR-P05-11), so this asks
   * for more with the cursor it was handed rather than reloading everything.
   * The cursor is only valid for this subject, which is what keeps "see more"
   * from ever changing which teacher or course is being read.
   */
  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const page = await load(subjectId, nextCursor);
      if (current.current !== subjectId) return;
      setReviews((shown) => {
        const seen = new Set(shown.map((r) => r.reviewId));
        return [...shown, ...(page.reviews ?? []).filter((r) => !seen.has(r.reviewId))];
      });
      setAggregate(page.aggregate);
      setNextCursor(page.nextCursor);
    } catch (err) {
      // What is already on screen stays; only the next page failed.
      setMoreError(err);
    } finally {
      setLoadingMore(false);
    }
  }, [load, subjectId, nextCursor]);

  if (loading) return <LoadingState what="las reseñas" compact />;
  if (error)
    return <ErrorState action="cargar las reseñas" error={error} onRetry={refresh} compact />;

  return (
    <section style={{ marginBottom: 24 }} aria-labelledby={`reviews-${subject}`}>
      <h3 id={`reviews-${subject}`} style={{ marginBottom: 8 }}>
        {title}
      </h3>

      {aggregate && aggregate.totalReviews > 0 ? (
        <div style={{ marginBottom: 12 }}>
          <StarRating
            value={aggregate.averageRating}
            count={aggregate.totalReviews}
            showValue
            showCount
          />
        </div>
      ) : (
        // Not a zero and not five stars. "No reviews yet" is a different
        // statement from "rated zero", and a badge showing 0.0 would be read
        // as the second.
        <p style={{ color: '#666', margin: 0 }}>Todavía no hay reseñas.</p>
      )}

      {reviews.map((review) => (
        <ReviewCard
          key={review.reviewId}
          // The server sends no reviewer, on purpose. What it does guarantee is
          // that they took the class, which is the part worth saying.
          reviewerName="Una familia verificada"
          rating={review.rating}
          text={review.comment ?? ''}
          date={new Date(review.createdAt).toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'long',
          })}
        />
      ))}

      {moreError ? (
        <ErrorState action="cargar más reseñas" error={moreError} onRetry={loadMore} compact />
      ) : (
        nextCursor && (
          <button type="button" onClick={loadMore} disabled={loadingMore} style={moreButtonStyle}>
            {loadingMore ? 'Cargando…' : 'Ver más reseñas'}
          </button>
        )
      )}
    </section>
  );
};

const moreButtonStyle: React.CSSProperties = {
  background: 'none',
  border: '1px solid #d1d5da',
  borderRadius: 6,
  color: '#0366d6',
  cursor: 'pointer',
  padding: '6px 12px',
  marginTop: 8,
  font: 'inherit',
};

export default ReviewsSection;
